import React from 'react';

export interface Memory {
  id: string;
  title: string;
  description?: string;
  mediaUrl: string; // Photo or video URL
  mediaType: 'image' | 'video';
  likes: number;
  date: string; // YYYY-MM-DD
  createdAt: string;
}

export interface FarmEvent {
  id: string;
  title: string;
  date: string; // User-friendly formatted date (e.g., 'Oct 14')
  dateRaw: string; // YYYY-MM-DD for sorting / admin editing
  description: string;
  longDescription?: string;
  thumbnailUrl: string;
  galleryUrls?: string[]; // Sub-gallery for the event details
  category: 'upcoming' | 'past';
  type?: string; // Tag like 'Family', 'Dinner', 'Maintenance', etc.
}

export interface FarmFeature {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  tag?: string;
  icon?: string;
}

export interface HomeSettings {
  hero_image_url: string;
  hero_title: string;
  hero_subtitle: string;
  hero_button_text: string;
}

export interface Feedback {
  id: string;
  username: string;
  feedback: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

export interface PhotoFilter {
  id: string;
  name: string;
  style: string;
}

export const PHOTO_FILTERS: PhotoFilter[] = [
  { id: 'normal', name: 'Original', style: 'none' },
  { id: 'vintage', name: 'Vintage', style: 'sepia(0.5) contrast(1.2) brightness(0.95) saturate(1.1)' },
  { id: 'bw', name: 'Mono', style: 'grayscale(1) contrast(1.2)' },
  { id: 'warm', name: 'Warm Sun', style: 'sepia(0.3) saturate(1.3) hue-rotate(-10deg) contrast(1.05)' },
  { id: 'cool', name: 'Cool Dusk', style: 'saturate(1.1) hue-rotate(15deg) brightness(0.95) contrast(1.1)' },
  { id: 'dramatic', name: 'Dramatic', style: 'contrast(1.4) saturate(1.2) brightness(0.9)' },
  { id: 'sepia', name: 'Sepia', style: 'sepia(0.8) contrast(1.1)' },
  { id: 'cinematic', name: 'Cinematic', style: 'contrast(1.15) saturate(0.85) brightness(1.05)' }
];

export function getImageStyleFromUrl(url?: string): React.CSSProperties {
  if (!url) return {};
  try {
    const hashIndex = url.indexOf('#');
    if (hashIndex === -1) return {};
    const filterId = url.substring(hashIndex + 1);
    const filter = PHOTO_FILTERS.find(f => f.id === filterId);
    if (filter && filter.style !== 'none') {
      return { filter: filter.style };
    }
  } catch (e) {
    // ignore
  }
  return {};
}

export function cleanImageUrl(url?: string): string {
  if (!url) return '';
  const hashIndex = url.indexOf('#');
  if (hashIndex === -1) return url;
  return url.substring(0, hashIndex);
}

export function getFilterFromUrl(url?: string): string {
  if (!url) return 'normal';
  let target = url;
  if (url.startsWith('[')) {
    try {
      const parsed = JSON.parse(url);
      if (Array.isArray(parsed) && parsed[0]) {
        target = parsed[0];
      }
    } catch (e) {}
  }
  const hashIndex = target.indexOf('#');
  if (hashIndex === -1) return 'normal';
  return target.substring(hashIndex + 1);
}

export function applyFilterToUrl(url: string, filterId: string): string {
  if (!url || filterId === 'normal') return url.split('#')[0];
  
  if (url.startsWith('[')) {
    try {
      const urls = JSON.parse(url);
      if (Array.isArray(urls)) {
        const mapped = urls.map(u => {
          const clean = u.split('#')[0];
          return `${clean}#${filterId}`;
        });
        return JSON.stringify(mapped);
      }
    } catch (e) {
      // ignore
    }
  }
  
  const clean = url.split('#')[0];
  return `${clean}#${filterId}`;
}
